import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, PencilIcon, Plus, Trash2 } from 'lucide-react';
import { Layout } from '@/components/Layout/Layout';
import { MainHeading } from '@/components/common/MainHeading';
import CommonTable, { Action, TableColumn } from '@/components/common/CommonTable';
import ConfirmationModal from '@/components/common/ConfirmationModal';
import { callApi, ThrowError } from '@/api/callApi';
import { API_ENDPOINT, API_URL } from '@/api/constant';
import { formatDateTime, showToastNotification, ResourceLabelMap } from '@/utility/helper';
import { usePermissions } from '@/hooks/usePermissions';
import { RESOURCES } from '@/context/permissions';
import { RoutesLink } from '@/utility/RouteLinks';
import { useActiveRole } from '@/hooks/useActiveRole';
import { IRole, IRoleListResponse } from '@/interfaces/Role/role.interface';

export const Roles = () => {
    const navigate = useNavigate();
    const { prefix } = useActiveRole();
    const { canCreate, canEdit, canDelete } = usePermissions();

    const [roles, setRoles] = useState<IRole[]>([]);
    const [loading, setLoading] = useState(false);
    const [roleToDelete, setRoleToDelete] = useState<string | null>(null);
    const [deleteLoading, setDeleteLoading] = useState(false);

    const fetchRoles = async () => {
        setLoading(true);
        try {
            const response = await callApi<IRoleListResponse>(
                `${API_URL}${API_ENDPOINT.roles}`,
                { method: 'GET' }
            );
            setRoles(response?.data || []);
        } catch (error) {
            ThrowError(error);
        } finally {
            setLoading(false);
        }
    };

    const deleteRole = async () => {
        if (!roleToDelete) return;
        setDeleteLoading(true);
        try {
            await callApi(
                `${API_URL}${API_ENDPOINT.roles}/${roleToDelete}`,
                { method: 'DELETE' }
            );
            showToastNotification('success', 'Role deleted successfully.');
            setRoles(prev => prev.filter(r => r.id !== roleToDelete));
        } catch (error) {
            ThrowError(error);
        } finally {
            setDeleteLoading(false);
            setRoleToDelete(null);
        }
    };

    useEffect(() => {
        fetchRoles();
    }, []);

    const columns: TableColumn<IRole>[] = [
        {
            key: 'name',
            header: 'Role Name',
            render: (item) => (
                <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-xs shrink-0">
                        {item.name.charAt(0).toUpperCase()}
                    </div>
                    <span className="font-semibold text-foreground text-sm capitalize">{item.name}</span>
                </div>
            ),
        },
        // {
        //     key: 'permissions',
        //     header: 'Permissions',
        //     render: (item) => (
        //         <div className="flex flex-wrap gap-1.5 max-w-xs">
        //             {item?.permissions && item?.permissions.slice(0, 4).map((p, i) => (
        //                 <span
        //                     key={i}
        //                     className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-medium bg-primary/10 text-primary capitalize border border-primary/20"
        //                 >
        //                     {ResourceLabelMap[p.resource] || p.resource}
        //                 </span>
        //             ))}
        //             {item?.permissions?.length > 4 && (
        //                 <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-medium bg-muted text-muted-foreground">
        //                     +{item?.permissions?.length - 4} more
        //                 </span>
        //             )}
        //         </div>
        //     ),
        // },
        {
            key: 'createdAt',
            header: 'Created',
            render: (item) => (
                <span className="text-xs text-muted-foreground">{formatDateTime(item.createdAt)}</span>
            ),
        },
    ];

    const actions: Action<IRole>[] = [
        {
            text: 'View',
            icon: <Eye strokeWidth={1.5} size={16} className='cursor-pointer' />,
            onClick: (item) => navigate(prefix + RoutesLink.ViewRole.replace(':id', item.id)),
        },
        ...(canEdit(RESOURCES.ROLE) ? [{
            text: 'Edit',
            icon: <PencilIcon strokeWidth={1.5} size={16} className='cursor-pointer' />,
            onClick: (item: IRole) => navigate(prefix + RoutesLink.EditRole.replace(':id', item.id)),
        }] : []),
        // ...(canDelete(RESOURCES.ROLE) ? [{
        //     text: 'Delete',
        //     icon: <Trash2 strokeWidth={1.5} size={16} className="text-destructive cursor-pointer" />,
        //     onClick: (item: IRole) => setRoleToDelete(item.id),
        // }] : []),
    ];

    return (
        <Layout>
            <MainHeading
                title="Roles"
                description="Manage staff roles and their access permissions"
                {...(canCreate(RESOURCES.ROLE) && {
                    actionLabel: 'Add Role',
                    actionIcon: <Plus className="w-4 h-4" />,
                    onAction: () => navigate(prefix + RoutesLink.AddRole),
                })}
            />

            <div className="mt-6">
                <CommonTable
                    columns={columns}
                    data={roles}
                    loading={loading}
                    emptyMessage="No roles found."
                    actions={actions}
                />
            </div>

            <ConfirmationModal
                open={!!roleToDelete}
                onOpenChange={(open) => !open && setRoleToDelete(null)}
                onConfirm={deleteRole}
                loading={deleteLoading}
                type="danger"
                title="Delete Role?"
                message="Are you sure you want to delete this role? Staff members assigned this role may lose access. This cannot be undone."
                confirmText="Yes, Delete"
                cancelText="Cancel"
                size="lg"
            />
        </Layout>
    );
};
